// The per-tonic spelling rule: how a scale's notes are named in a given key.
//
// A scale formula (scaleFormulas.ts) is tonic-agnostic. Spelled in a key, each
// degree takes the letter its step number counts up from the tonic's letter
// (step 3 above D is F, whatever its accidental), and the accidental that puts
// that letter on the degree's pitch. So D Dorian is D E F G A B C, B Locrian's
// second is C (never B#), and F# Lydian's fourth is B#. The rule is a pure
// function of (formula, tonic): no per-key spelling is stored anywhere.
//
// One exception, where the rule would need three accidentals: a double-flatted
// degree on a step the key already flats. Among the 80 playable units x 12
// tonics this happens twice, the sixth of Ultralocrian bb6 and of Superlocrian
// bb6 on Db (B triple-flat). That note is respelled to the same pitch on the
// neighbouring letter with the fewest accidentals, keeping the accidental's
// direction on a tie (A flat).

import type { Tonic } from 'Types/ExerciseContentTypes';
import { SEMITONES_PER_OCTAVE, degreeSemitones, parseDegree, scaleFormula } from './scaleFormulas';

export type NoteLetter = 'C' | 'D' | 'E' | 'F' | 'G' | 'A' | 'B';

/** The other name of each black-key tonic label (C# for Db, Gb for F#). */
export type EnharmonicTonic = 'C#' | 'D#' | 'G#' | 'A#' | 'Gb';

/** A name a scale can be spelled from: a cell's tonic label or its enharmonic name. */
export type TonicName = Tonic | EnharmonicTonic;

const LETTERS: readonly NoteLetter[] = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];

const NATURAL_PITCH_CLASS: Record<NoteLetter, number> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

/** The most accidentals a spelled note carries (a double sharp or double flat). */
export const MAX_ACCIDENTALS = 2;

export interface SpelledNote {
  letter: NoteLetter;
  /** Semitones off the natural letter: -2 (double flat) to +2 (double sharp). */
  alteration: number;
  /** 0-11, C = 0. */
  pitchClass: number;
}

const pitchClassOf = (value: number): number =>
  ((value % SEMITONES_PER_OCTAVE) + SEMITONES_PER_OCTAVE) % SEMITONES_PER_OCTAVE;

/** The alteration that puts `letter` on `pitchClass`, in -6..5. */
const alterationFor = (letter: NoteLetter, pitchClass: number): number => {
  const gap = pitchClassOf(pitchClass - NATURAL_PITCH_CLASS[letter]);
  return gap > SEMITONES_PER_OCTAVE / 2 ? gap - SEMITONES_PER_OCTAVE : gap;
};

const parseTonic = (tonic: TonicName): SpelledNote => {
  const letter = tonic[0] as NoteLetter;
  const accidentals = tonic.slice(1);
  const alteration = accidentals.startsWith('#') ? accidentals.length : -accidentals.length;
  return { letter, alteration, pitchClass: pitchClassOf(NATURAL_PITCH_CLASS[letter] + alteration) };
};

/** The same pitch on the letter needing the fewest accidentals; a tie keeps the
 *  direction of the alteration it replaces. */
const respelled = (pitchClass: number, direction: number): SpelledNote => {
  const [best] = LETTERS.map((letter) => ({
    letter,
    alteration: alterationFor(letter, pitchClass),
    pitchClass,
  })).sort(
    (a, b) =>
      Math.abs(a.alteration) - Math.abs(b.alteration) ||
      Number(Math.sign(b.alteration) === Math.sign(direction)) -
        Number(Math.sign(a.alteration) === Math.sign(direction)),
  );
  return best;
};

/** The unit's notes spelled in the tonic's key, ascending from the tonic. */
export const spellScale = (unitId: string, tonic: TonicName): SpelledNote[] => {
  const root = parseTonic(tonic);
  const rootLetterIndex = LETTERS.indexOf(root.letter);
  return scaleFormula(unitId).map((degree) => {
    const { step } = parseDegree(degree);
    const letter = LETTERS[(rootLetterIndex + step - 1) % LETTERS.length];
    const pitchClass = pitchClassOf(root.pitchClass + degreeSemitones(degree));
    const alteration = alterationFor(letter, pitchClass);
    return Math.abs(alteration) > MAX_ACCIDENTALS
      ? respelled(pitchClass, alteration)
      : { letter, alteration, pitchClass };
  });
};

/** A spelled note's name in plain text: 'C', 'Bb', 'F#', 'Ebb', 'C##'. */
export const noteName = (note: SpelledNote): string =>
  `${note.letter}${(note.alteration > 0 ? '#' : 'b').repeat(Math.abs(note.alteration))}`;
